/**
 * 水质报告轮播：手动切页 + 分页进度条动画
 * 进度条：animating-a/b 交替归零（免重排）、默认 paused，--running 控制播放，与 setTimeout 切页同步
 * 首次进入视口后开始自动播放，悬停与离开视口均不暂停
 */
class WdWaterReportSlider extends HTMLElement {
  static INTERVAL_DEFAULT = 5000
  static ANIMATING_CLASSES = ['wd-water-report__page--animating-a', 'wd-water-report__page--animating-b']

  connectedCallback() {
    this._abortController = new AbortController()

    this.interval = Number(this.dataset.interval) || WdWaterReportSlider.INTERVAL_DEFAULT
    this._remainingTime = this.interval
    this._animPhase = 'b'
    this._hasStarted = false
    this._timerId = null
    this._timerStartedAt = 0
    this.slider = null
    this._observer = null

    const splideSlider = this.querySelector('splide-slider')
    if (!splideSlider) return

    const onReady = nextSlider => {
      if (!nextSlider || this.slider) return
      this.initSlider(nextSlider)
    }

    // Splide 可能已初始化，否则等待 splide:loaded
    if (splideSlider.slider) {
      onReady(splideSlider.slider)
    } else {
      splideSlider.addEventListener(
        'splide:loaded',
        e => {
          const nextSlider = e.detail?.slider
          if (nextSlider) onReady(nextSlider)
        },
        { once: true, signal: this._abortController.signal }
      )
    }
  }

  disconnectedCallback() {
    this.cleanup()
  }

  initSlider(slider) {
    this.slider = slider
    this.style.setProperty('--progress-duration', `${this.interval}ms`)

    // move：过渡开始，先停计时；moved：过渡结束，重置进度并重新调度
    this.slider.on('move', () => this.clearTimer())
    this.slider.on('moved', () => {
      this._remainingTime = this.interval
      this.resetProgressAnimation()
      if (this._hasStarted) {
        this.startProgressAnimation()
        this.scheduleNext(this.interval)
      }
    })

    this.classList.add('wd-water-report--ready', 'wd-water-report--paused')
    this.resetProgressAnimation()
    this.initObserver()
  }

  getActivePaginationPage() {
    return this.querySelector('.wd-water-report__pagination .splide__pagination__page.is-active')
  }

  hasAnimatingClass(page) {
    return WdWaterReportSlider.ANIMATING_CLASSES.some(className => page.classList.contains(className))
  }

  /** 在 a/b 间切换 animation-name，浏览器会新建动画实例，无需 offsetWidth 触发重排 */
  getNextAnimatingClass() {
    this._animPhase = this._animPhase === 'b' ? 'a' : 'b'
    return `wd-water-report__page--animating-${this._animPhase}`
  }

  /** 挂载动画到当前圆点并归零，保持 paused（无 --running） */
  resetProgressAnimation() {
    if (!this.classList.contains('wd-water-report--ready')) return

    this.querySelectorAll('.wd-water-report__pagination .splide__pagination__page').forEach(page => {
      page.classList.remove(...WdWaterReportSlider.ANIMATING_CLASSES, 'wd-water-report__page--running')
    })

    const activePage = this.getActivePaginationPage()
    if (!activePage) return

    activePage.classList.add(this.getNextAnimatingClass())
  }

  /** 满足运行条件时为当前圆点加上 --running，CSS animation-play-state 变为 running */
  startProgressAnimation() {
    if (!this.canRunTimer()) return

    const activePage = this.getActivePaginationPage()
    if (!activePage || !this.hasAnimatingClass(activePage)) return

    activePage.classList.add('wd-water-report__page--running')
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) {
      this.onFirstVisible()
      return
    }

    // 观察轮播卡片自身，首次进入视口后启动自动播放
    this._observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry?.isIntersecting) this.onFirstVisible()
      },
      { rootMargin: '200px', threshold: 0 }
    )
    this._observer.observe(this)
  }

  clearTimer() {
    if (!this._timerId) return
    clearTimeout(this._timerId)
    this._timerId = null
  }

  canRunTimer() {
    return this.slider && this.classList.contains('wd-water-report--ready') && this._hasStarted
  }

  scheduleNext(delay) {
    this.clearTimer()
    if (!this.canRunTimer()) return

    this._remainingTime = delay ?? this.interval
    this._timerStartedAt = Date.now()
    this._timerId = setTimeout(() => {
      this._remainingTime = this.interval
      this.slider.go('>')
    }, this._remainingTime)
  }

  /** 首次进入视口后启动，之后不再暂停 */
  onFirstVisible() {
    if (this._hasStarted) return
    this._hasStarted = true

    this._observer?.disconnect()
    this._observer = null

    this.classList.remove('wd-water-report--paused')
    this.startProgressAnimation()
    this.scheduleNext(this._remainingTime)
  }

  cleanup() {
    this.clearTimer()
    this._observer?.disconnect()
    this._observer = null
    this._abortController?.abort()
    this._abortController = null
    this.slider = null
    this.classList.remove('wd-water-report--paused', 'wd-water-report--ready')
    this.querySelectorAll(
      '.wd-water-report__page--animating-a, .wd-water-report__page--animating-b, .wd-water-report__page--running'
    ).forEach(page => {
      page.classList.remove(...WdWaterReportSlider.ANIMATING_CLASSES, 'wd-water-report__page--running')
    })
  }
}

if (!customElements.get('wd-water-report-slider')) {
  customElements.define('wd-water-report-slider', WdWaterReportSlider)
}
